import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateMessageTranscription } from 'src/store/modules/chat/actions';
import { useCopilotAnswer } from '../../contexts/copilotAnswer/context';
import { TranscriptionService } from './transcription-service';

interface UseTranscriptionProps {
  audioUrl: string;
  currentTranscription?: string;
}

export function useTranscription({
  audioUrl,
  currentTranscription,
}: Readonly<UseTranscriptionProps>) {
  const TRY_AGAIN_MESSAGE =
    'Não foi possível transcrever o áudio, tente novamente em alguns instantes...';
  const DISABLED_TEMPORARY_MESSAGE =
    'Transcrição indisponível. Tente novamente mais tarde!';

  const MAX_TRY = 3;
  const MAX_TIME_TRY = 10000; // 10 segundos em milissegundos

  const [transcription, setTranscription] = useState(
    currentTranscription ?? ''
  );
  const [isTranscribed, setIsTranscribed] = useState(
    !!currentTranscription || false
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [countTry, setCountTry] = useState(0);
  const [canTryAgain, setCanTryAgain] = useState(true);
  const { handleCreateCopilotAnswer } = useCopilotAnswer();
  const dispatch = useDispatch();

  const handleTranscription = useCallback(
    async (currentChat: any, messageUuid: string) => {
      setIsLoading(true);
      try {
        const file = (await TranscriptionService.getFile(
          audioUrl,
          messageUuid
        )) as unknown as File;

        const response = await TranscriptionService.transcription(
          file,
          messageUuid
        );
        if (response.transcription) {
          setTranscription(response.transcription);
          setIsTranscribed(true);
          setError(false);
          setCanTryAgain(true);

          const lastTwentyMessages = currentChat?.messages?.slice(-20);
          const lastMessagesIndex = lastTwentyMessages.length - 1;
          const lastMessage = lastTwentyMessages[lastMessagesIndex];
          const lastTwentyMessagesPayload = [
            ...lastTwentyMessages.slice(0, lastMessagesIndex),
            {
              ...lastMessage,
              attachments:
                lastMessage?.attachments?.length > 0
                  ? [...lastMessage.attachments]
                  : [
                      {
                        type: lastMessage.type,
                        data: {
                          transcription: {
                            transcription: response.transcription,
                          },
                        },
                      },
                    ],
            },
          ];
          // Só transcreve se for uma mensagem receptiva e a última mensagem da lista de 20 mensagens
          if (
            lastTwentyMessages[lastMessagesIndex]?.cuid === messageUuid &&
            lastTwentyMessages[lastMessagesIndex]?.dir === 'i'
          ) {
            handleCreateCopilotAnswer(
              lastTwentyMessagesPayload,
              currentChat?.id
            );
          }

          // atualiza a mensagem com a transcrição na store
          dispatch(
            updateMessageTranscription({
              contact_id: currentChat?.id,
              message_cuid: messageUuid,
              ...response,
            })
          );
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      } finally {
        setIsLoading(false);
        setCountTry((prev) => prev + 1);
      }
    },
    [audioUrl, dispatch]
  );

  useEffect(() => {
    if (countTry >= MAX_TRY) {
      setCanTryAgain(false);
    }
  }, [countTry]);

  useEffect(() => {
    if (!canTryAgain) {
      setTimeout(() => {
        setCountTry(0);
        setCanTryAgain(true);
      }, MAX_TIME_TRY);
    }
  }, [canTryAgain]);

  /**
   * Retorna o texto para ser exibido no componente TranscriptionText
   * caso esteja carregando, retorna 'Transcrevendo...'
   * caso ocorra um erro e ainda puder tentar novamente, retorna 'Não foi possível transcrever o áudio, tente novamente...'
   * caso ocorra um erro e não puder tentar novamente, retorna 'Transcrição indisponível. Tente novamente mais tarde!'
   * caso tenha uma transcrição, retorna a transcrição
   * @returns string
   */
  function getText() {
    if (error) return TRY_AGAIN_MESSAGE;
    if (error && !canTryAgain) return DISABLED_TEMPORARY_MESSAGE;
    if (transcription) return transcription;
    return '';
  }

  return {
    // states
    isLoading,
    error,
    canTryAgain,
    isTranscribed,
    // functions
    handleTranscription,
    getText,
  };
}
